import { Controller } from "@hotwired/stimulus"
import { fetch as turboFetch } from "@hotwired/turbo"
import Sortable from "sortablejs"

// Board drag & drop. Each column declares the property value cards dropped
// into it should receive (state_id / priority / assignee_id / cycle_id, or a
// teamspace board's state_name).
// With swimlanes, each [data-kanban-row] also declares a sub-property value;
// cross-row drags update both. Rows whose sub-property isn't draggable
// (label/created_by) get per-row Sortable groups so cards stay in-row.
// FD-925: keepOrder (a teamspace board) makes a drop change only the column's
// and row's values, never the card's order. editableProjects, when set, lists
// the projects whose cards ([data-project-id]) may be picked up at all.
// FD-1272: props and peekPath (set only by the work-item board) say how this board
// draws a card, so board_move can send the moved card back drawn the same way.
export default class extends Controller {
  static values = {
    property: String, subProperty: String, keepOrder: Boolean, editableProjects: Array,
    props: Array, peekPath: String
  }

  connect() {
    // FD-630: columns are (re)bound through a Map so a broadcast morph that
    // inserts or replaces a column reconciles instead of leaving it undroppable —
    // this controller sits on the surviving wrapper, so connect() fires only once
    // and a one-shot querySelectorAll snapshot would go stale.
    this.sortables = new Map()
    this.queue = null
    // FD-629: idiomorph morphs the board's nodes IN PLACE, so nothing about a
    // node says a morph happened. Count them instead — an awaited board_move
    // needs to know whether fresh server totals landed underneath it.
    this.morphs = 0
    this.bindColumns()
    this.onMorph = () => {
      this.morphs += 1
      this.bindColumns()
    }
    document.addEventListener("turbo:morph", this.onMorph)
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.onMorph)
    this.sortables.forEach((sortable) => sortable.destroy())
    this.sortables.clear()
  }

  // Bind a Sortable to every column that doesn't have one, and reap entries
  // whose column a morph detached (keeping them would leak Sortables pointing
  // at nodes no longer in the document).
  bindColumns() {
    for (const [column, sortable] of this.sortables) {
      if (!column.isConnected) {
        sortable.destroy()
        this.sortables.delete(column)
      }
    }
    this.element.querySelectorAll("[data-kanban-column]").forEach((column) => {
      if (this.sortables.has(column)) return
      this.sortables.set(column, Sortable.create(column, this.sortableOptions(column)))
    })
  }

  sortableOptions(column) {
    const options = {
      group: this.groupName(column),
      animation: 150,
      ghostClass: "sortable-ghost",
      draggable: "[data-item-url]",
      onEnd: (event) => this.dropped(event)
    }
    // FD-925: no reordering within a column, since there is no order to save.
    if (this.keepOrderValue) options.sort = false
    if (this.hasEditableProjectsValue) {
      // Sortable asks this on every press and won't start a drag on a locked card.
      // preventOnFilter off, so the press still reaches the card's link and menu.
      options.filter = (_event, card) => this.refusesDrag(card)
      options.preventOnFilter = false
    }
    return options
  }

  // FD-925: true when the card is in a project this board doesn't list as one the
  // viewer can edit. Only a board that lists them locks anything; the server refuses
  // such a move anyway (403), this just keeps the card from moving at all.
  refusesDrag(card) {
    if (!card || !this.hasEditableProjectsValue) return false
    return !this.editableProjectsValue.map(String).includes(card.dataset.projectId)
  }

  // FD-630: the per-row group name has to come from SERVER-rendered state. A
  // JS-stamped id is stripped by every morph, so a column a later morph inserted
  // would be given a fresh id — a group none of its row-mates' Sortables belong
  // to — and stay undroppable from them, which is the failure bindColumns()
  // exists to prevent. data-kanban-sub-value is unique per row (groups_for emits
  // one row per distinct sub-group plus a single nil bucket) and morph-stable.
  groupName(column) {
    const row = column.closest("[data-kanban-row]")
    if (!row || this.subPropertyValue) return "kanban"
    return `kanban-row-${row.dataset.kanbanSubValue ?? ""}`
  }

  // FD-630: one board_move at a time — two optimistic DOM moves in flight would
  // race each other's revert and count deltas. Queue the second drop rather than
  // discarding it: reverting it snapped the card back exactly like a REJECTED
  // move, so a drag that was never even sent looked identical to one the server
  // refused (the silent failure FD-449's snap-back exists to make visible).
  dropped(event) {
    const run = () => this.move(event).catch(() => {})
    this.queue = this.queue ? this.queue.then(run) : run()
    return this.queue
  }

  async move(event) {
    const morphsAtStart = this.morphs
    const card = event.item
    const column = event.to
    const url = card.dataset.itemUrl
    const property = this.propertyValue || "state_id"
    const body = { [property]: column.dataset.kanbanValue ?? "" }

    const row = column.closest("[data-kanban-row]")
    if (this.subPropertyValue && row) body[this.subPropertyValue] = row.dataset.kanbanSubValue ?? ""

    // FD-925: with keepOrder a drop sends only what it changed: the column's value
    // when the column changed, the row's when the row changed, and nothing at all
    // for a drop back where it was. Sending the row's assignee again would drop
    // the card's other assignees.
    if (this.keepOrderValue) {
      if ((event.from?.dataset?.kanbanValue ?? "") === body[property]) delete body[property]
      const fromRow = event.from?.closest?.("[data-kanban-row]")
      if (this.subPropertyValue && fromRow && (fromRow.dataset.kanbanSubValue ?? "") === body[this.subPropertyValue]) {
        delete body[this.subPropertyValue]
      }
      if (Object.keys(body).length === 0) return
    }

    const originalSortOrder = card.dataset.sortOrder
    const nextEl = card.nextElementSibling
    const nextCard = nextEl?.matches?.("[data-item-url]") ? nextEl : null
    // FD-629: in a paginated column (load-more sentinel present) the loaded cards
    // stop at PAGE_SIZE, so an insert-to-end has no `next` to split against and
    // previous + 100 lands the card mid-group among the unloaded items (only other
    // viewers see it — FD-489 suppresses the actor's own refresh). Send an explicit
    // end position and let the server compute past-the-end of the FULL column.
    // FD-925: keepOrder sends neither, so the card keeps its sort_order.
    if (!this.keepOrderValue) {
      if (!nextCard && column.querySelector("[id^='wi-loadmore-']")) {
        body.position = "end"
      } else {
        const previous = card.previousElementSibling?.dataset?.sortOrder
        body.sort_order = this.between(previous, nextCard?.dataset?.sortOrder)
        card.dataset.sortOrder = body.sort_order
      }
    }

    // FD-1272: ask for the moved card back, drawn as this board draws it. Added after
    // the keepOrder check above, so a drop that changed nothing still sends nothing.
    if (this.hasPropsValue) body.card = { props: this.propsValue, peek_path: this.peekPathValue }

    // FD-449: SortableJS already moved the card in the DOM. If the server REJECTS the
    // move (422 — e.g. an approval-gated/invalid transition) or the request fails, put
    // the card back where it came from instead of leaving the board out of sync with
    // the DB until a reload. (gantt/calendar drags get the same treatment.)
    let response
    try {
      // FD-489: turboFetch (Turbo's fetch wrapper) sends X-Turbo-Request-Id and registers
      // it in Turbo's recentRequests, so the refresh broadcast triggered by this PATCH is
      // recognized as our own and skipped — the drag already moved the DOM; without it the
      // actor's client full-refreshes the whole board after every drop.
      response = await turboFetch(`${url}/board_move`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      })
      // response.redirected too: an expired session answers a PATCH with a 302 to the sign-in
      // page, fetch follows it by default, and the resulting 200 makes response.ok TRUE — so
      // the card would settle into its new column having persisted nothing, silently. Every
      // failure on this path has to be visible as a snap-back.
      if (!response.ok || response.redirected) {
        this.revert(event, originalSortOrder)
        return this.showRefusal(response)
      }
    } catch {
      return this.revert(event, originalSortOrder)
    }

    // FD-1272: the move is saved from here on, so nothing below may revert it; a body
    // that doesn't parse (an older server's 204) just skips the mirroring and redraw.
    // The saved order is mirrored onto the card so a follow-up drag splits against the
    // true value (FD-629: with position=end the server computed it).
    const data = await response.json().catch(() => null)
    if (data?.sort_order != null && card.isConnected) card.dataset.sortOrder = data.sort_order

    this.adjustCounts(event.from, event.to, card, morphsAtStart)
    if (typeof data?.card_html === "string") this.redrawCard(card, column, data.card_html)
  }

  // FD-1272: show the move on the card itself (state, priority, assignees, sprint)
  // now, not at the next refresh, from the server's render of the saved card.
  // The node is KEPT and only its contents and data change. SortableJS may still hold
  // it: a drop queued behind this one carries it as event.item, and a drag already
  // under way moves it; a replacement node would leave either working on a detached
  // copy (a wrong sort_order, a revert that does nothing, or two cards with one id).
  // Skipped when a morph has put the card in another column meanwhile, so it never
  // shows a state its column contradicts, and when its menu is open, so the menu stays
  // (the "…" menu is a <cs-dropdown> since FD-1295; a <details> menu counts too).
  redrawCard(card, column, html) {
    if (!card.isConnected || card.closest("[data-kanban-column]") !== column) return
    if (card.querySelector("details[open], cs-dropdown[open]")) return
    const template = document.createElement("template")
    template.innerHTML = html
    const fresh = template.content.firstElementChild
    if (!fresh || fresh.id !== card.id) return
    // class is left alone: SortableJS keeps its drag state there.
    for (const { name, value } of fresh.attributes) {
      if (name !== "class") card.setAttribute(name, value)
    }
    card.replaceChildren(...fresh.childNodes)
  }

  // Return the dragged card to its origin column + index (and restore its sort order),
  // so a rejected/failed board_move doesn't leave a phantom move on screen.
  revert(event, originalSortOrder) {
    // FD-630: a morph can land during the awaited PATCH and replace these nodes —
    // reinserting the card into a detached column would make it vanish until
    // reload, and the morphed board already shows the server's answer anyway.
    if (!event.from?.isConnected || !event.item.isConnected) return
    // SortableJS fires onEnd after the DOM move, so on a same-column upward drag the card
    // sits at newIndex and its original successor has shifted to oldIndex+1; using oldIndex
    // directly would reinsert one position off. Downward and cross-column moves are unaffected.
    const idx = event.from === event.to && event.newIndex < event.oldIndex ? event.oldIndex + 1 : event.oldIndex
    const ref = event.from.children[idx] || null
    event.from.insertBefore(event.item, ref)
    if (originalSortOrder === undefined) delete event.item.dataset.sortOrder
    else event.item.dataset.sortOrder = originalSortOrder
  }

  // FD-45/FD-925: a refused drop says why. board_move answers a refusal with
  // { errors: [...] }, and the first one is shown as an error toast, as
  // shared/_flash_now renders one. Set as text, never markup: the message can
  // repeat what was sent (a state's name). A redirect (an expired session) or a
  // body with no errors (a bare 403) just snaps back.
  async showRefusal(response) {
    if (response.redirected) return
    const data = await response.json().catch(() => null)
    const message = data?.errors?.[0]
    const flash = document.getElementById("flash")
    if (typeof message !== "string" || message === "" || !flash) return

    const toast = document.createElement("div")
    toast.className = "toast toast-top toast-center z-50"
    toast.dataset.controller = "autohide"
    const alert = document.createElement("div")
    alert.className = "alert alert-error text-[13px] py-2 rounded-md"
    alert.textContent = message
    toast.append(alert)
    flash.append(toast)
  }

  between(previous, next) {
    const lo = previous ? parseFloat(previous) : null
    const hi = next ? parseFloat(next) : null
    if (lo != null && hi != null) return (lo + hi) / 2
    if (lo != null) return lo + 100
    if (hi != null) return hi / 2
    return 65535
  }

  // FD-629: the badges render the SERVER-side totals; recounting loaded cards
  // clobbered every paginated column (a 40-item column read "25" after any drop
  // anywhere on the board). A successful cross-column drop changes each affected
  // total by exactly one, so adjust those two badges by delta instead.
  adjustCounts(from, to, card, morphsAtStart) {
    if (!from || !to || from === to) return
    if (!from.isConnected || !to.isConnected) return
    // A broadcast morph can land inside the awaited PATCH and rewrite both badges
    // from fresh server totals; applying the delta on top of those leaves them off
    // by one until a reload — the very symptom FD-629 was filed for. isConnected
    // cannot see it (idiomorph morphs the columns in place), so compare the morph
    // counter. The delta is still owed only when that render PREDATED our move,
    // and the card says which: idiomorph pairs cards by the stable per-item id
    // _item_card/_item_row render, so a server render that did NOT include our
    // move puts the card back in `from` (or drops this node entirely).
    if (this.morphs !== morphsAtStart && card.closest("[data-kanban-column]") === to) return
    this.bumpCount(from, -1)
    this.bumpCount(to, 1)
  }

  bumpCount(column, delta) {
    const badge = column.closest("[data-kanban-group]")?.querySelector("[data-kanban-count]")
    const total = parseInt(badge?.textContent, 10)
    if (!Number.isNaN(total)) badge.textContent = Math.max(0, total + delta)
  }
}
